import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';

import LoadingIndicator from 'components/Loading/LoadingIndicator';
import FilterMenu from 'components/FilterMenu/FilterMenu';
import AssetGraph from 'components/Graph/AssetGraph';

import { AssetChartStore } from 'stores/workflowStores/newWorkflowPlanStore/assetChartStore';
import { ChartPeriods, chartPeriods } from 'common/interfaces';
import { Currency } from 'enums';
import { DateTime } from 'luxon';

interface Props {
  woodId: string;
  store: AssetChartStore;
  currency: Currency;
}

const useStyles = makeStyles(() =>
  createStyles({
    loader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
    },
  }),
);

const AssetChart: React.FC<Props> = ({ woodId, store, currency }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<ChartPeriods>(ChartPeriods.LAST_5Y);
  const classes = useStyles();
  const chartRef = useRef<Highcharts.Chart | null>();
  const filterStyles = { fontSize: '80%' };

  useEffect(() => {
    (async () => {
      await store.getData(woodId);
    })();
  }, [store, woodId]);

  const data = store.chartsData[woodId];

  useEffect(() => {
    if (data && data.length > 0) {
      const periodObj = chartPeriods.find((p) => p.value === period);
      const lastValue = data[data.length - 1];

      const defaultShift = { years: 5 };
      const minDate =
        periodObj?.start ??
        DateTime.fromFormat(lastValue.date, 'yyyy-MM-dd')
          .minus(periodObj?.shift ?? defaultShift)
          .toMillis();
      const minValue = data.findIndex((value) => DateTime.fromFormat(value.date, 'yyyy-MM-dd').toMillis() >= minDate);

      const values: number[] = [];
      const { current: chart } = chartRef;
      if (chart) {
        chart.xAxis[0].setExtremes(Math.max(minValue, 0), data.length - 1);
        chart.series[0].points.forEach((point) => {
          if (
            point.index >= Math.max(minValue, 0) &&
            point.index <= data.length - 1 &&
            point.y !== null &&
            point.y !== undefined
          ) {
            values.push(point.y);
          }
        });
        chart.yAxis[0].setExtremes(Math.min(...values), Math.max(...values));
        const displaySize = data.length - Math.max(minValue, 0);
        const tickInterval = Math.floor(displaySize / Math.min(displaySize, 7));
        chart.xAxis[0].update({ tickInterval });
      }
      /*
      console.log(
        `last:${JSON.stringify(lastValue)}`,
        `shift:${JSON.stringify(shift)}`,
        `minDate:${minDate}`,
        `minValue:${minValue}`,
        `x:${Math.max(minValue, 0)}-${data.length - 1}`,
        `y:${Math.min(...values)}-${Math.max(...values)}`,
      );
      */
    }
  }, [period, woodId, data]);

  return (
    <>
      {data && data.length > 0 ? (
        <AssetGraph
          data={data}
          currency={currency}
          callback={(chart) => {
            chartRef.current = chart;
          }}
        />
      ) : (
        <div className={classes.loader}>{!data ? <LoadingIndicator size={56} /> : t('accountDetail_graph_noData')}</div>
      )}
      <FilterMenu
        filterStyles={filterStyles}
        onClick={setPeriod}
        items={chartPeriods}
        currentValue={period}
        disabled={!data || !data.length}
      />
    </>
  );
};

export default observer(AssetChart);
